// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Contact resolution — where the feel of the game actually lives.
//
// One general 3-D impulse routine serves every contact: a wall (normal in the playfield
// plane), the playfield surface itself (normal straight up), a ramp wall, the glass. The
// same code produces a wall bounce, a rolling ball, and a live catch, because those are
// not three behaviours — they are one contact model under three conditions.
//
// See 02 §7.

import type { Ball } from './Ball'
import type { Material } from './materials'
import { effectiveRestitution, MATERIAL_NAMES } from './materials'
import type { Rng } from '../rng/Rng'
import {
  BALL_TANGENT_INV_MASS,
  MIN_SEPARATION_VELOCITY,
  PENETRATION_CORRECTION,
} from './constants'
import { sinCos } from '../math/deterministic'

/**
 * A surface a ball can strike: the seven materials, plus another ball.
 *
 * Ball-against-ball is not a material — both sides are the same steel, and what makes it
 * distinctive is that there are two of them. 07 §3.3 gives it its own recipe for exactly that
 * reason, so it needs a code the material table cannot supply.
 */
export const SURFACE_BALL = MATERIAL_NAMES.length

/** How many surface codes exist, for a reader sizing a table by them. */
export const SURFACE_COUNT = MATERIAL_NAMES.length + 1

/**
 * A presentation-only report that a ball struck something.
 *
 * The sibling of `EventQueue.tap`, and separate from it on purpose. `EventQueue` is the
 * machine's **switch matrix** — the channel 04 §9 defines between physics and rules — and a
 * real machine has no switch under a rubber. Putting contacts through it would hand every
 * rulesheet a vocabulary no physical machine has, and would push a thousand entries a second
 * into a queue the rule engine drains and reallocates every step.
 *
 * So contacts have their own channel: no queue, no allocation, and nothing in the simulation
 * reads it. Undefined during replay validation, during the soak and in every core test, which
 * makes one `undefined` check the whole cost of having it.
 *
 * `speed` is the normal impact speed before resolution, m/s — the number a sound's gain and a
 * controller's haptic strength are supposed to be computed from rather than guessed at.
 */
export type ContactTap = (ball: number, surface: number, speed: number, step: number) => void

/** What a resolved contact did, for sound, haptics, rule events and reaction torque. */
export interface ContactResult {
  /** Normal impact speed before resolution, m/s. Drives impact sound gain. */
  impactSpeed: number
  /** Magnitude of the normal impulse applied. */
  normalImpulse: number
  /** True when the contact was a resting one rather than a genuine impact. */
  resting: boolean
  /**
   * Total impulse delivered to the ball, planar components.
   *
   * A moving collider must receive the equal and opposite reaction, which is what makes
   * a fast ball genuinely slow a mid-stroke flipper — and therefore what makes coil
   * strength *feel* like strength rather than read as a number.
   */
  impulseX: number
  impulseY: number
}

const RESULT: ContactResult = {
  impactSpeed: 0,
  normalImpulse: 0,
  resting: false,
  impulseX: 0,
  impulseY: 0,
}

/**
 * A collider the ball can genuinely push back, and by how much.
 *
 * **Every other surface in the machine is the machine.** A wall, a post, a ramp wall and the
 * glass are bolted to a cabinet on a floor, so their effective mass at a contact is the
 * planet's and the ball's own inverse mass is the whole denominator. A flipper bat is not:
 * about its pivot it has `I = 1.08e-4 kg·m²`, which at a mid-bat contact radius is an
 * effective 0.10 kg against a 0.081 kg ball. It is the *lighter* of the two bodies in the
 * collision, and it has to be solved as one.
 *
 * `rx, ry` is the contact point relative to the body's pivot, in the playfield plane; the
 * lever arm the routine needs is `(r × n)_z`, which is that vector crossed with whichever
 * direction the impulse acts along.
 */
export interface ReactingBody {
  /** Inverse rotational inertia about the pivot, (kg·m²)⁻¹. */
  readonly invInertia: number
  readonly rx: number
  readonly ry: number
}

/**
 * Apply one contact impulse to a ball.
 *
 * `nx, ny, nz` is the unit normal pointing from the surface toward the ball centre.
 * `svx, svy, svz` is the velocity of the surface at the contact point — a moving flipper,
 * a spinning spinner, or the whole machine frame during a nudge. Passing that velocity in
 * here is the entire mechanism by which nudging works: no special case anywhere touches
 * the ball directly.
 *
 * `body` is the collider's own inertia, for the one collider in the machine that has any:
 * the flipper. Omit it and the surface is immovable, which is the truth for everything else.
 *
 * Returns a shared result object. Read it before the next call.
 */
export function applyContactImpulse(
  ball: Ball,
  nx: number,
  ny: number,
  nz: number,
  material: Material,
  svx: number,
  svy: number,
  svz: number,
  rng: Rng,
  /** Surface scatter angle in radians, or −1 to use the material's. See `Collider.scatter`. */
  scatterOverride = -1,
  body?: ReactingBody,
): ContactResult {
  const r = ball.radius

  // The collider's share of the contact. Zero for every immovable surface, which collapses
  // both denominators below to the ball's own inverse mass and reproduces the wall exactly.
  const bodyInvInertia = body === undefined ? 0 : body.invInertia
  const bodyRx = body === undefined ? 0 : body.rx
  const bodyRy = body === undefined ? 0 : body.ry

  // Vector from the ball centre to the contact point: straight down the inward normal.
  const rx = -nx * r
  const ry = -ny * r
  const rz = -nz * r

  // Velocity of the ball's material at the contact point: v + ω × r.
  const wxr_x = ball.wy * rz - ball.wz * ry
  const wxr_y = ball.wz * rx - ball.wx * rz
  const wxr_z = ball.wx * ry - ball.wy * rx

  const relx = ball.vx + wxr_x - svx
  const rely = ball.vy + wxr_y - svy
  const relz = ball.vz + wxr_z - svz

  const vn = relx * nx + rely * ny + relz * nz

  RESULT.impactSpeed = vn < 0 ? -vn : 0
  RESULT.normalImpulse = 0
  RESULT.resting = false
  RESULT.impulseX = 0
  RESULT.impulseY = 0

  // Already separating — nothing to do. Positional correction is the caller's job.
  if (vn >= 0) return RESULT

  const approach = -vn

  // A resting contact must not bounce, or a cradled ball jitters forever and PH-A01 fails.
  const resting = approach < MIN_SEPARATION_VELOCITY
  RESULT.resting = resting
  const e = resting ? 0 : effectiveRestitution(material, approach)

  // The normal passes through the centre of a sphere, so it produces no torque on the *ball*
  // and its share of the denominator is simply `1/m`. The collider's share is the lever arm
  // squared over its inertia — zero unless it is a flipper.
  //
  // **Leaving the collider out of this denominator was a genuine defect, not a simplification
  // (M1-fix, 19 Aug 2026).** The bat was infinitely massive to the ball and finitely massive
  // to itself: the ball rebounded off it as though off a wall, and then the *same* impulse was
  // handed to the bat as recoil. A 1.2 m/s ball carrying 0.058 J left a resting bat spinning at
  // 61 rad/s, which is 0.20 J — the contact was manufacturing three and a half times the energy
  // that arrived. Everything a yielding bat is supposed to do was unreachable while that held.
  const leverN = bodyRx * ny - bodyRy * nx
  const jn = (-(1 + e) * vn) / (ball.invMass + leverN * leverN * bodyInvInertia)

  ball.vx += jn * nx * ball.invMass
  ball.vy += jn * ny * ball.invMass
  ball.vz += jn * nz * ball.invMass

  RESULT.normalImpulse = jn
  RESULT.impulseX = jn * nx
  RESULT.impulseY = jn * ny

  // --- friction -----------------------------------------------------------
  //
  // This one block produces an extraordinary amount of the game's texture. A ball
  // touching high-friction flipper rubber converts linear momentum into angular
  // momentum and loses a lot of speed — that is what a live catch physically *is*.
  // A ball on the playfield transitions from sliding to rolling. A backhand works
  // because the bat's surface velocity at the contact point exceeds the ball's.

  const tvx = relx - vn * nx
  const tvy = rely - vn * ny
  const tvz = relz - vn * nz
  const tSpeed = Math.sqrt(tvx * tvx + tvy * tvy + tvz * tvz)

  if (tSpeed > 1e-9 && material.friction > 0) {
    const inv = 1 / tSpeed
    const tx = tvx * inv
    const ty = tvy * inv
    const tz = tvz * inv

    // Impulse that would exactly kill the tangential slip. The ball's share is
    // `1/m + r²/I`; the collider's is its own lever arm along the tangent, for the same
    // reason and with the same consequence as the normal denominator above.
    const leverT = bodyRx * ty - bodyRy * tx
    let jt = -tSpeed / (BALL_TANGENT_INV_MASS + leverT * leverT * bodyInvInertia)

    // Coulomb limit. Beyond it the surfaces slide instead of gripping.
    const limit = material.friction * jn
    if (jt < -limit) jt = -limit

    const jx = jt * tx
    const jy = jt * ty
    const jz = jt * tz

    ball.vx += jx * ball.invMass
    ball.vy += jy * ball.invMass
    ball.vz += jz * ball.invMass

    // Torque: ω += I⁻¹ (r × J). This is the spin coupling.
    ball.wx += ball.invInertia * (ry * jz - rz * jy)
    ball.wy += ball.invInertia * (rz * jx - rx * jz)
    ball.wz += ball.invInertia * (rx * jy - ry * jx)

    RESULT.impulseX += jx
    RESULT.impulseY += jy
  }

  // --- scatter ------------------------------------------------------------
  //
  // A small randomised deflection standing in for the microscopic imperfection of a real
  // playfield. Essential to outlane behaviour: a perfectly deterministic outlane is
  // either always fatal or never fatal, and both are wrong.
  //
  // Drawn from the replay PRNG, so a replay reproduces its scatter exactly.
  const scatter = scatterOverride >= 0 ? scatterOverride : material.scatter
  if (scatter > 0 && !resting) {
    const angle = rng.nextSigned(scatter)
    const { sin: s, cos: c } = sinCos(angle)
    const vx = ball.vx
    const vy = ball.vy
    ball.vx = vx * c - vy * s
    ball.vy = vx * s + vy * c
  }

  return RESULT
}

/**
 * Push a ball out of penetration.
 *
 * Positional correction is separate from the impulse so that a ball wedged between two
 * colliders is pushed out by both without either one's push being counted as an impact.
 */
export function correctPenetration(
  ball: Ball,
  nx: number,
  ny: number,
  nz: number,
  depth: number,
): void {
  if (depth <= 0) return
  // `PENETRATION_CORRECTION` is documented as the fraction applied per step and was read by
  // nothing at all, so the knob existed and did not turn: somebody chasing contact jitter would
  // have changed it and watched the simulation ignore them. It is 1.0, so wiring it changes no
  // result — multiplying by one is exact — and it is now the number it says it is.
  const push = depth * PENETRATION_CORRECTION
  ball.x += nx * push
  ball.y += ny * push
  ball.z += nz * push
}

/**
 * Rolling resistance.
 *
 * Contact friction alone brings a ball to *rolling*, not to rest — a rolling ball has no
 * slip for Coulomb friction to act on. Real balls still slow down, because the ball and
 * the playfield deform very slightly at the contact patch. Modelled as a small torque
 * opposing rotation while the ball is in sustained surface contact.
 */
export function applyRollingResistance(ball: Ball, coefficient: number, dt: number): void {
  const decay = 1 - coefficient * dt
  if (decay <= 0) {
    ball.wx = 0
    ball.wy = 0
    ball.wz = 0
    return
  }
  ball.wx *= decay
  ball.wy *= decay
  ball.wz *= decay
}
